import { useMemo } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HOTELS_QUERY_KEY,
  getIntakeStatus,
  listHotels,
  triggerIntake,
  type Hotel,
  type IntakeStatus,
} from '../features/hotels/api.js';
import { getFavoriteHotels, toggleFavoriteHotel } from '../features/dashboard/api.js';
import { Button } from '../components/ui/button.js';
import { EditorialEmpty } from '../components/editorial-empty.js';
import { StarIcon } from '../components/ui/icons.js';

function HotelsList() {
  const { data, isLoading, error } = useQuery<Hotel[]>({
    queryKey: HOTELS_QUERY_KEY,
    queryFn: listHotels,
  });
  const favoritesQ = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });
  const favSet = useMemo(() => new Set(favoritesQ.data ?? []), [favoritesQ.data]);

  // Sort hotels: favorites first, then alphabetical by name (HU locale).
  // Hooks must run unconditionally — keep this above any early return so
  // loading/error states don't shift the hook order between renders.
  const hotels = useMemo(() => {
    const list = (data ?? []).slice();
    list.sort((a, b) => {
      const aFav = favSet.has(a.id);
      const bFav = favSet.has(b.id);
      if (aFav !== bFav) return aFav ? -1 : 1;
      return a.name.localeCompare(b.name, 'hu');
    });
    return list;
  }, [data, favSet]);

  if (isLoading) return <p className="text-body text-[var(--fg-2)]">Betöltés…</p>;
  if (error) return <p className="text-body text-[var(--danger)]">{(error as Error).message}</p>;

  const featured = hotels[0];
  const rest = hotels.slice(1);

  return (
    <div className="space-y-6">
      {/* HEADER — editorial */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="h2-eyebrow">Portfólió</div>
          <h1 className="mt-1.5 text-h1 font-sans font-medium leading-none tracking-tight text-[var(--ink-700)]">
            Hoteljeid{' '}
            <span className="font-mono text-h2 font-normal text-[var(--fg-faint)]">
              {hotels.length}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/approvals" search={{ view: 'links' }}>
            <Button variant="outline">Ügyfél-naptár linkek</Button>
          </Link>
          <Link to="/app/hotels/new">
            <Button>+ Új hotel</Button>
          </Link>
        </div>
      </div>

      {hotels.length === 0 && (
        <EditorialEmpty
          glyph="H"
          title="Még nincs hotel"
          body={
            <>
              Hozz létre egy hotelt az{' '}
              <strong className="text-[var(--ink-700)]">{'„+ Új hotel"'}</strong> gombbal, és
              indulhat a tartalomtervezés.
            </>
          }
        />
      )}

      {/* FEATURED HERO */}
      {featured && <FeaturedHero hotel={featured} isFavorite={favSet.has(featured.id)} />}

      {/* BENTO GRID */}
      {rest.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rest.map((h) => (
            <BentoHotel key={h.id} hotel={h} isFavorite={favSet.has(h.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Compact crawl status pill + "Crawl" / "Újra" trigger button.
 * Polls /hotels/:id/intake/status while a job is active so the badge
 * reflects the current state without a page refresh.
 */
function CrawlChip({ hotel, dark = false }: { hotel: Hotel; dark?: boolean }) {
  const qc = useQueryClient();
  const hasWebsite = !!hotel.websiteUrl;
  const statusQ = useQuery<IntakeStatus>({
    queryKey: ['intake-status', hotel.id],
    queryFn: () => getIntakeStatus(hotel.id),
    // No need to hammer the API for hotels without a website URL — they
    // can never have a crawl in flight.
    enabled: hasWebsite,
    // Only re-poll while an active job is in flight. Idle hotels settle to
    // false (no polling), preventing the throttler from rejecting bursts
    // when the user has many hotels open.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'pending' || s === 'running' ? 4_000 : false;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const trigger = useMutation({
    mutationFn: () => triggerIntake(hotel.id, hotel.websiteUrl ?? ''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['intake-status', hotel.id] });
      void qc.invalidateQueries({ queryKey: HOTELS_QUERY_KEY });
    },
  });

  const status = statusQ.data?.status;
  const busy = status === 'pending' || status === 'running' || trigger.isPending;

  const labelByStatus: Record<string, string> = {
    pending: 'Várólistán',
    running: 'Crawl…',
    done: 'Friss',
    failed: 'Hiba',
  };
  // Use the --st-* status tokens (they flip for dark mode) instead of hardcoded
  // Tailwind pastel literals, which stay light and wash out on the dark surface.
  const toneByStatus: Record<string, string> = {
    pending: 'bg-[var(--st-topic-pending-bg)] text-[var(--st-topic-pending-fg)]',
    running: 'bg-[var(--st-topic-pending-bg)] text-[var(--st-topic-pending-fg)]',
    done: 'bg-[var(--st-ready-bg)] text-[var(--st-ready-fg)]',
    failed: 'bg-[var(--st-rejected-bg)] text-[var(--st-rejected-fg)]',
  };
  const overlayTone = dark
    ? 'bg-white/15 text-white hover:bg-white/25 border-white/30'
    : 'bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)] border-[var(--app-divider)]';

  function onClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!hasWebsite || busy) return;
    trigger.mutate();
  }

  return (
    <div className="flex items-center gap-1.5">
      {status && (
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-meta font-semibold uppercase tracking-wide ${toneByStatus[status] ?? 'bg-gray-100 text-gray-700'}`}
        >
          {(status === 'pending' || status === 'running') && (
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current opacity-70" />
          )}
          {labelByStatus[status] ?? status}
        </span>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={!hasWebsite || busy}
        title={
          !hasWebsite
            ? 'Nincs weboldal URL — előbb állítsd be a profilban'
            : statusQ.data?.finishedAt
              ? 'Weboldal újra olvasása ugyanazzal az URL-lel'
              : 'Weboldal beolvasása'
        }
        className={`inline-flex h-7 items-center gap-1 rounded-md border px-2.5 text-meta font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${overlayTone}`}
      >
        {trigger.isPending
          ? 'Indítás…'
          : status === 'pending' || status === 'running'
            ? 'Folyamatban'
            : statusQ.data?.finishedAt
              ? 'Weboldal újra olvasása'
              : 'Weboldal beolvasása'}
      </button>
      {trigger.isError && (
        <span
          className={`text-meta font-medium ${dark ? 'text-red-200' : 'text-red-700'}`}
          title={(trigger.error as Error).message}
        >
          Hiba
        </span>
      )}
    </div>
  );
}

/**
 * Star button that pins/unpins a hotel in the current user's favorites.
 * Favorites are unlimited; the dashboard hero trims to its own slot count.
 * Writes the server's returned list straight into the query cache so every
 * list/hero/filter consumer re-sorts without a refetch.
 */
function FavoriteToggle({
  hotelId,
  isFavorite,
  dark = false,
}: {
  hotelId: string;
  isFavorite: boolean;
  dark?: boolean;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => toggleFavoriteHotel(hotelId),
    onSuccess: (res) => {
      qc.setQueryData(['dashboard-favorites'], res.favoriteHotelIds);
    },
  });
  const label = isFavorite ? 'Eltávolítás a kedvencek közül' : 'Kedvencekhez adás';
  const base =
    'inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-60';
  const tone = dark
    ? isFavorite
      ? 'border-[var(--accent-highlight)] bg-[var(--accent-highlight)] text-[var(--ink-900)]'
      : 'border-white/30 bg-white/10 text-white hover:bg-white/20'
    : isFavorite
      ? 'border-[var(--brand-500)] bg-[var(--brand-50)] text-[var(--brand-700)] hover:bg-[var(--brand-100)]'
      : 'border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]';
  return (
    <button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      aria-pressed={isFavorite}
      aria-label={label}
      title={label}
      data-testid="favorite-toggle"
      className={`${base} ${tone}`}
    >
      <StarIcon size={14} filled={isFavorite} />
    </button>
  );
}

function FeaturedHero({ hotel, isFavorite }: { hotel: Hotel; isFavorite: boolean }) {
  return (
    <div
      className="h2-hotel-hero relative"
      style={{
        backgroundImage: hotel.coverImageUrl ? `url('${hotel.coverImageUrl}')` : undefined,
      }}
    >
      <div className="flex w-full flex-wrap items-end justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-2">
            <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-meta font-semibold uppercase tracking-wider text-white/85">
              kiemelt
            </span>
            {isFavorite && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-highlight)] px-2.5 py-0.5 text-meta font-semibold uppercase tracking-wider text-[var(--ink-900)]"
                title="Kedvenc — a listák elején jelenik meg"
              >
                <StarIcon size={12} filled /> Kedvenc
              </span>
            )}
            <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-meta font-semibold uppercase tracking-wider text-white/85">
              {hotel.defaultPostFrequencyPerWeek} poszt / hét
            </span>
          </div>
          <Link
            to="/app/hotels/$hotelId"
            params={{ hotelId: hotel.id }}
            className="mt-3 block font-serif text-display font-medium leading-none tracking-tight text-white hover:opacity-90"
          >
            {hotel.name}
          </Link>
          <div className="mt-2 flex items-center gap-3 text-body text-white/80">
            {hotel.websiteUrl && (
              <span className="font-mono">{new URL(hotel.websiteUrl).host}</span>
            )}
            <span>
              Brand/Sales {Math.round(hotel.brandSalesRatio * 100)}%/
              {Math.round((1 - hotel.brandSalesRatio) * 100)}%
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <FavoriteToggle hotelId={hotel.id} isFavorite={isFavorite} dark />
            <CrawlChip hotel={hotel} dark />
          </div>
          <Link to="/app/hotels/$hotelId" params={{ hotelId: hotel.id }}>
            <span className="inline-flex h-9 items-center gap-1.5 rounded-md border border-white/30 bg-white/10 px-4 text-body font-medium text-white backdrop-blur-sm">
              Profil megnyitása →
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function BentoHotel({ hotel, isFavorite }: { hotel: Hotel; isFavorite: boolean }) {
  return (
    <div
      className={`h2-bento group relative block text-left ${hotel.isActive ? '' : 'opacity-60 grayscale-[40%]'}`}
    >
      <Link
        to="/app/hotels/$hotelId"
        params={{ hotelId: hotel.id }}
        className="block focus:outline-none"
      >
        <div
          className="relative bg-[var(--app-bg)]"
          style={{
            aspectRatio: '5 / 3',
            backgroundImage: hotel.coverImageUrl ? `url('${hotel.coverImageUrl}')` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {!hotel.coverImageUrl && (
            <div className="flex h-full items-center justify-center">
              <span className="text-meta font-medium uppercase tracking-wider text-[var(--fg-faint)]">
                Nincs kép — futtass crawl-t
              </span>
            </div>
          )}
          <div className="absolute right-2 top-2 flex items-center gap-1">
            {isFavorite && (
              <span
                className="inline-flex h-[20px] items-center justify-center rounded-full bg-[var(--accent-highlight)] px-1.5 text-meta font-bold text-[var(--ink-900)] shadow-sm"
                aria-label="Kedvenc hotel"
                title="Kedvenc — a listák elején jelenik meg"
              >
                <StarIcon size={12} filled />
              </span>
            )}
            {!hotel.isActive && (
              <span className="inline-flex items-center rounded-full bg-[var(--app-surface-2)] px-2 py-0.5 text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
                Inaktív
              </span>
            )}
            <span
              className="inline-flex h-[20px] items-center gap-1 rounded-full bg-[var(--app-surface)] px-2 text-meta font-bold text-[var(--brand-500)] shadow-sm"
              aria-label={`${hotel.defaultPostFrequencyPerWeek} poszt hetente`}
              title={`${hotel.defaultPostFrequencyPerWeek} poszt / hét`}
            >
              <span className="font-mono">{hotel.defaultPostFrequencyPerWeek}</span>
              <span className="text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
                / hét
              </span>
            </span>
          </div>
        </div>
        <div className="p-3 pb-2">
          <div className="truncate text-body font-semibold leading-tight text-[var(--ink-700)]">
            {hotel.name}
          </div>
          <div className="mt-0.5 text-meta text-[var(--fg-faint)]">
            {hotel.websiteUrl ? new URL(hotel.websiteUrl).host : hotel.slug}
          </div>
          <div className="mt-2 flex h-1 overflow-hidden rounded-sm bg-[var(--app-divider)]">
            <div
              className="h-full bg-[var(--brand-500)]"
              style={{ width: `${Math.round(hotel.brandSalesRatio * 100)}%` }}
            />
            <div
              className="h-full bg-[var(--accent-highlight)]"
              style={{ width: `${Math.round((1 - hotel.brandSalesRatio) * 100)}%` }}
            />
          </div>
        </div>
      </Link>
      {/* Action row outside the Link so the button's click doesn't navigate. */}
      <div className="flex items-center justify-between gap-2 border-t border-[var(--app-divider)] px-3 py-2">
        <CrawlChip hotel={hotel} />
        <FavoriteToggle hotelId={hotel.id} isFavorite={isFavorite} />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/hotels/')({
  component: HotelsList,
});
